import { useEffect, useState } from 'react';
import { Alert, Button } from '@playerbase/ui';
import type {
  DiscordGuildChannel,
  DiscordGuildRole,
  FeedbackForm,
  FormChannelPerm,
  FormRecruitChannelFailure,
  FormRecruitRoleFailure,
} from '@playerbase/types';

import { requestFormPermissionCheck } from '../../../games/feedback-forms-data.js';

// Channel-access panel.
//
// State machine driven by feedback_forms.last_perm_check_result, written
// by the bot's form_perm_check_listener. The panel is hidden when both
// channels report all green; otherwise it surfaces one row per failing
// channel using the copy from .docs/feedback-forms/channel-access-ux.md.
//
// The Re-check button calls request_form_permission_check (RPC enforces a
// 30s server-side floor); we honour the same window with a visible
// cooldown so misclicks don't waste round-trips.

interface ChannelAccessPanelProps {
  form: FeedbackForm;
  channels: DiscordGuildChannel[];
  guildId: string | null;
}

const COOLDOWN_SECONDS = 30;

function isAllGreen(perm: FormChannelPerm | null | undefined): boolean {
  if (!perm) return false;
  return perm.exists && perm.botIsMember && perm.canSend && perm.canEmbed;
}

function isMissingChannel(perm: FormChannelPerm | null | undefined): boolean {
  if (!perm) return false;
  // exists:false from the bot is "404 / actually deleted"; bot logs the
  // private case as exists:true,botIsMember:false,canView:false, so we
  // distinguish "deleted" from "private" using exists.
  return !perm.exists;
}

function isPrivateNoMember(perm: FormChannelPerm | null | undefined): boolean {
  if (!perm) return false;
  return perm.exists && (!perm.botIsMember || !perm.canView);
}

interface FailingChannel {
  role: 'survey' | 'response';
  perm: FormChannelPerm | null;
  channelId: string | null;
  channelName: string;
}

function findChannelName(
  channels: DiscordGuildChannel[],
  channelId: string | null,
  perm: FormChannelPerm | null,
): string {
  if (channelId !== null) {
    const match = channels.find((c) => c.channelId === channelId);
    if (match !== undefined) return match.name;
  }
  if (perm !== null && perm.channelId.length > 0) {
    const matchByPerm = channels.find((c) => c.channelId === perm.channelId);
    if (matchByPerm !== undefined) return matchByPerm.name;
  }
  return 'this channel';
}

export function ChannelAccessPanel({
  form,
  channels,
  guildId,
}: ChannelAccessPanelProps): JSX.Element | null {
  const result = form.lastPermCheckResult;

  const survey: FailingChannel = {
    role: 'survey',
    perm: result?.surveyChannel ?? null,
    channelId: form.surveyChannelId,
    channelName: findChannelName(channels, form.surveyChannelId, result?.surveyChannel ?? null),
  };
  const response: FailingChannel = {
    role: 'response',
    perm: result?.responseChannel ?? null,
    channelId: form.responseChannelId,
    channelName: findChannelName(channels, form.responseChannelId, result?.responseChannel ?? null),
  };

  // No check has ever run AND no channels picked yet → nothing to show.
  // No check has ever run but channels are picked → surface a hint to
  // re-check (the builder fires perm checks on channel-change so this is
  // a transient state).
  if (result === null) {
    if (form.surveyChannelId === null && form.responseChannelId === null) return null;
    return <NoCheckYetPanel formId={form.id} />;
  }

  const failing: FailingChannel[] = [];
  if (form.surveyChannelId !== null && !isAllGreen(survey.perm)) failing.push(survey);
  if (form.responseChannelId !== null && !isAllGreen(response.perm)) failing.push(response);

  // All channel perms are green - but the Enable RPC also enforces a
  // 5-minute freshness window on lastPermCheckAt. Surface a stand-alone
  // Re-check panel so the user can clear that gate without going through
  // a Save (the only other trigger). Skip when RecruitAccessPanel will
  // already render its own re-check (recruit configured but the perm
  // check pre-dates recruit) so we don't double up.
  if (failing.length === 0) {
    const recruitConfigured =
      form.recruitChannelIds.length > 0 || form.recruitRoleIds.length > 0;
    const recruitPanelWillSurface = recruitConfigured && result.recruit === null;
    if (!permsAreEnableReady(form) && !recruitPanelWillSurface) {
      return (
        <StalePermsPanel formId={form.id} lastCheckedAt={form.lastPermCheckAt} />
      );
    }
    return null;
  }

  return (
    <div className="rounded-md border border-warning bg-warning-surface px-4 py-3">
      <h3 className="mb-2 font-display text-sm font-semibold text-warning-text">
        PlayerBase needs more access
      </h3>
      <div className="flex flex-col gap-3">
        {failing.map((entry) => (
          <ChannelRow key={entry.role} entry={entry} guildId={guildId} />
        ))}
      </div>
      <RecheckBar formId={form.id} lastCheckedAt={form.lastPermCheckAt} />
    </div>
  );
}

function NoCheckYetPanel({ formId }: { formId: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-surface px-4 py-3">
      <p className="text-sm text-text-muted">
        Permissions haven&rsquo;t been checked yet. Run a check before enabling.
      </p>
      <RecheckBar formId={formId} lastCheckedAt={null} />
    </div>
  );
}

function StalePermsPanel({
  formId,
  lastCheckedAt,
}: {
  formId: string;
  lastCheckedAt: string | null;
}): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-surface px-4 py-3">
      <p className="text-sm text-text-muted">
        Permissions are out of date. Re-check before enabling.
      </p>
      <RecheckBar formId={formId} lastCheckedAt={lastCheckedAt} />
    </div>
  );
}

function ChannelRow({
  entry,
  guildId,
}: {
  entry: FailingChannel;
  guildId: string | null;
}): JSX.Element {
  const role = entry.role === 'survey' ? 'Survey channel' : 'Response channel';

  if (isMissingChannel(entry.perm)) {
    return (
      <div className="text-sm text-text">
        <p>
          <strong>{role}: #{entry.channelName} no longer exists.</strong>
        </p>
        <p className="text-text-muted">
          Pick a different channel above.
        </p>
      </div>
    );
  }

  if (isPrivateNoMember(entry.perm)) {
    return (
      <div className="text-sm text-text">
        <p>
          <strong>{role}: PlayerBase needs to be added to #{entry.channelName}.</strong>
        </p>
        <p className="text-text-muted">
          Add the PlayerBase bot in <em>Edit Channel → Permissions</em>, then grant{' '}
          <strong>Send Messages</strong>.
        </p>
        <DiscordChannelLink guildId={guildId} channelId={entry.perm?.channelId ?? null} />
      </div>
    );
  }

  // State C: bot is in the channel but missing Send / Embed.
  const missing: string[] = [];
  if (entry.perm !== null) {
    if (!entry.perm.canSend) missing.push('Send Messages');
    if (!entry.perm.canEmbed) missing.push('Embed Links');
  }

  return (
    <div className="text-sm text-text">
      <p>
        <strong>{role}: PlayerBase is in #{entry.channelName} but can&rsquo;t post.</strong>
      </p>
      <p className="text-text-muted">
        Grant the PlayerBase role these permissions in #{entry.channelName}:
        {' '}
        {missing.length === 0 ? 'review channel permissions.' : missing.join(', ') + '.'}
      </p>
      <DiscordChannelLink guildId={guildId} channelId={entry.perm?.channelId ?? null} />
    </div>
  );
}

function DiscordChannelLink({
  guildId,
  channelId,
}: {
  guildId: string | null;
  channelId: string | null;
}): JSX.Element | null {
  if (guildId === null || channelId === null || channelId.length === 0) return null;
  const href = `https://discord.com/channels/${guildId}/${channelId}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-xs text-accent hover:underline"
    >
      Open in Discord →
    </a>
  );
}

function RecheckBar({
  formId,
  lastCheckedAt,
}: {
  formId: string;
  lastCheckedAt: string | null;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(() => {
    if (lastCheckedAt === null) return null;
    const checked = new Date(lastCheckedAt).getTime();
    const elapsed = (Date.now() - checked) / 1000;
    if (elapsed >= COOLDOWN_SECONDS) return null;
    return checked + COOLDOWN_SECONDS * 1000;
  });
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (cooldownUntil === null) return undefined;
    function tick(): void {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil(((cooldownUntil ?? now) - now) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) setCooldownUntil(null);
    }
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [cooldownUntil]);

  async function handleClick(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await requestFormPermissionCheck(formId);
    setBusy(false);
    if (!result.ok) {
      setError('Could not request a permission check. Try again in a moment.');
      return;
    }
    setCooldownUntil(Date.now() + COOLDOWN_SECONDS * 1000);
  }

  return (
    <div className="mt-3 flex items-center gap-2">
      <Button
        variant="ghost"
        type="button"
        onClick={() => void handleClick()}
        disabled={busy || cooldownUntil !== null}
      >
        {busy
          ? 'Checking...'
          : cooldownUntil !== null
            ? `Re-check (${secondsLeft}s)`
            : 'Re-check'}
      </Button>
      {error !== null ? (
        <Alert tone="danger">{error}</Alert>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recruit access panel.
//
// Sister component to ChannelAccessPanel. Surfaces the recruit
// pre-flight result (added in migration 0043) inside the Recruit card
// so the studio sees exactly which Discord-side perms they need to
// grant before Enable will accept the form. Hidden when the form has
// no recruit configured OR the pre-flight came back fully clean.
//
// The panel reads the same `last_perm_check_result` as the channel
// version (one perm-check covers both surfaces); the Re-check button
// triggers the same RPC. We deliberately render two panels rather
// than one merged surface because a studio might have a clean recruit
// block while the survey channel is still flagged (or vice-versa) -
// keeping them adjacent to each card lets the studio fix what they're
// looking at without scrolling.
// ---------------------------------------------------------------------------

interface RecruitAccessPanelProps {
  form: FeedbackForm;
  channels: DiscordGuildChannel[];
  roles: DiscordGuildRole[];
  guildId: string | null;
}

function findChannelLabel(
  channels: DiscordGuildChannel[],
  channelId: string,
): string {
  const match = channels.find((c) => c.channelId === channelId);
  return match?.name ?? channelId;
}

function findRoleLabel(
  roles: DiscordGuildRole[],
  roleId: string,
): string {
  const match = roles.find((r) => r.externalId === roleId);
  return match?.name ?? roleId;
}

function recruitRoleAdvice(reason: FormRecruitRoleFailure['reason']): string {
  switch (reason) {
    case 'missing':
      return 'This role no longer exists - remove it above.';
    case 'managed':
      return "Discord owns this role (it's tied to a bot or integration), so PlayerBase can't assign it. Remove it above.";
    case 'above-bot':
      return "PlayerBase's role needs to sit above this one in your Discord role list.";
  }
}

function recruitChannelAdvice(
  reason: FormRecruitChannelFailure['reason'],
): string {
  switch (reason) {
    case 'missing':
      return 'This channel no longer exists or PlayerBase can no longer see it - remove it above.';
    case 'no-manage':
      return 'Grant the PlayerBase role Manage Channel on this channel so it can edit permission overwrites.';
  }
}

export function RecruitAccessPanel({
  form,
  channels,
  roles,
  guildId,
}: RecruitAccessPanelProps): JSX.Element | null {
  const recruitConfigured =
    form.recruitChannelIds.length > 0 || form.recruitRoleIds.length > 0;
  if (!recruitConfigured) return null;

  const result = form.lastPermCheckResult;
  if (result === null) {
    return (
      <div className="rounded-md border border-border bg-surface px-4 py-3">
        <p className="text-sm text-text-muted">
          PlayerBase hasn&rsquo;t checked recruit perms yet. Run a check before
          enabling.
        </p>
        <RecheckBar formId={form.id} lastCheckedAt={null} />
      </div>
    );
  }

  const recruit = result.recruit ?? null;
  if (recruit === null) {
    // Pre-recruit perm-check rows. Force a recheck.
    return (
      <div className="rounded-md border border-border bg-surface px-4 py-3">
        <p className="text-sm text-text-muted">
          The last permission check pre-dates recruit. Re-check to see whether
          PlayerBase can grant access.
        </p>
        <RecheckBar formId={form.id} lastCheckedAt={form.lastPermCheckAt} />
      </div>
    );
  }

  const totalFailures =
    recruit.missingGuildPerms.length
    + recruit.roleFailures.length
    + recruit.channelFailures.length;
  if (totalFailures === 0) return null;

  return (
    <div className="rounded-md border border-warning bg-warning-surface px-4 py-3">
      <h3 className="mb-2 font-display text-sm font-semibold text-warning-text">
        PlayerBase needs more access to recruit
      </h3>
      <div className="flex flex-col gap-3">
        {recruit.missingGuildPerms.includes('manage-roles') ? (
          <div className="text-sm text-text">
            <p>
              <strong>PlayerBase needs Manage Roles on this server.</strong>
            </p>
            <p className="text-text-muted">
              In <em>Server Settings → Roles</em>, edit the PlayerBase role and
              tick <strong>Manage Roles</strong>.
            </p>
          </div>
        ) : null}
        {recruit.roleFailures.map((failure) => (
          <div key={`role:${failure.roleId}`} className="text-sm text-text">
            <p>
              <strong>
                Role <code>{findRoleLabel(roles, failure.roleId)}</code>{' '}
                can&rsquo;t be assigned.
              </strong>
            </p>
            <p className="text-text-muted">{recruitRoleAdvice(failure.reason)}</p>
          </div>
        ))}
        {recruit.channelFailures.map((failure) => (
          <div key={`channel:${failure.channelId}`} className="text-sm text-text">
            <p>
              <strong>
                #{findChannelLabel(channels, failure.channelId)} can&rsquo;t be
                granted to players.
              </strong>
            </p>
            <p className="text-text-muted">
              {recruitChannelAdvice(failure.reason)}
            </p>
            <DiscordChannelLink guildId={guildId} channelId={failure.channelId} />
          </div>
        ))}
      </div>
      <RecheckBar formId={form.id} lastCheckedAt={form.lastPermCheckAt} />
    </div>
  );
}

// Helper: convenience predicate the builder uses to decide whether the
// Enable button can light up. Mirrors the server-side check in
// enable_feedback_form (0032 + 0043).
export function permsAreEnableReady(form: FeedbackForm): boolean {
  if (form.lastPermCheckAt === null) return false;
  const checkedAt = new Date(form.lastPermCheckAt).getTime();
  if (Number.isNaN(checkedAt)) return false;
  if (Date.now() - checkedAt > 5 * 60 * 1000) return false;
  const result = form.lastPermCheckResult;
  if (result === null) return false;
  if (
    !result.surveyChannel.canSend
    || !result.surveyChannel.canEmbed
    || !result.responseChannel.canSend
    || !result.responseChannel.canEmbed
  ) {
    return false;
  }
  // Recruit gate. Skip when the form has no recruit configured. When
  // configured, the bot must have written a recruit block AND every
  // failure array must be empty.
  const recruitConfigured =
    form.recruitChannelIds.length > 0 || form.recruitRoleIds.length > 0;
  if (!recruitConfigured) return true;
  const recruit = result.recruit ?? null;
  if (recruit === null) return false;
  return (
    recruit.missingGuildPerms.length === 0
    && recruit.roleFailures.length === 0
    && recruit.channelFailures.length === 0
  );
}
